import { getReferenceString } from "@medplum/core";
import { ChargeItem, Condition, Procedure } from "@medplum/fhirtypes";

import {
  findSourceProcedure,
  getProcedureModifiers,
  resolveChargeItemModifiers,
} from "@/billing/utils/charge-item-modifiers";
import { resolveChargeItemNote } from "@/billing/utils/charge-item-notes";
import { Extensions } from "@/fhir/enums/extensions";
import {
  getCodeableConceptCode,
  getCodeableConceptDisplay,
  getCodeableConceptSystem,
} from "@/utils/medplum";

import { EncounterChargeItemRow } from "../types/encounter-charge-item-rows";
import { ProcedureCodeEditTarget } from "../types/procedures";

import { getActiveChargeItems } from "./encounter-charge-items";
import { readProcedureNoteSource } from "./procedure-notes";

/** Units a charge falls back to when it carries no quantity, matching what capture writes. */
export const DEFAULT_CHARGE_ITEM_ROW_UNITS = 1;

type BuildEncounterChargeItemRowsParams = {
  /** The encounter's captured charges, in the order they should be listed. */
  chargeItems: ChargeItem[];
  /** The encounter's active procedures — the clinical side each charge was captured from. */
  procedures: Procedure[];
  /** The encounter's active diagnoses, which the procedures point at through `reasonReference`. */
  conditions: Condition[];
};

/** Indexes resources by their reference string, which is how the charges point at them. */
function toResourcesByReference<T extends Procedure | Condition>(resources: T[]): Map<string, T> {
  return new Map(
    resources.reduce<[string, T][]>((entries, resource) => {
      const reference = getReferenceString(resource);
      if (reference) entries.push([reference, resource]);
      return entries;
    }, []),
  );
}

/**
 * The diagnoses a Procedure was recorded under, as the codings a picker session takes.
 *
 * Reads `reasonReference` in its own order, which is the order the picker linked them in, and skips
 * anything that does not resolve to a diagnosis the encounter still holds. A retired diagnosis is
 * not a current reason to bill. Deduplicated by code, because a procedure linked to two diagnoses
 * sharing a code would otherwise list it twice.
 */
function toDiagnosisCodings(
  procedure: Procedure | undefined,
  conditionsByReference: Map<string, Condition>,
): ProcedureCodeEditTarget["conditions"] {
  const codings = new Map<string, ProcedureCodeEditTarget["conditions"][number]>();

  for (const { reference } of procedure?.reasonReference ?? []) {
    const condition = reference ? conditionsByReference.get(reference) : undefined;
    const code = getCodeableConceptCode(condition?.code);

    if (!code || codings.has(code)) continue;

    codings.set(code, {
      code,
      system: getCodeableConceptSystem(condition?.code),
      display: getCodeableConceptDisplay(condition?.code),
    });
  }

  return [...codings.values()];
}

/**
 * Returns the notes a row carries: the charge's own when it holds any, and the source Procedure's
 * otherwise.
 *
 * Capture copies the notes onto the charge and every biller edit rewrites them there, so the charge
 * is the current answer whenever it has one. A charge with no note of its own falls back to the
 * Procedure, which is still accurate: an edit that changes a note also rewrites the row's own
 * Procedure with the same notes and points the charge at it first, so a note cleared on the charge
 * is cleared there too. That covers charges captured or edited before notes were copied, whose notes
 * live only on the Procedure.
 */
function toRowNotes(
  chargeItem: ChargeItem,
  procedure: Procedure | undefined,
): Pick<ProcedureCodeEditTarget["procedure"], "billerNotes" | "providerNotes"> {
  const chargeNotes = {
    billerNotes: resolveChargeItemNote(chargeItem, Extensions.ChargeItemBillerNotes),
    providerNotes: resolveChargeItemNote(chargeItem, Extensions.ChargeItemProviderNotes),
  };

  if (chargeNotes.billerNotes || chargeNotes.providerNotes) return chargeNotes;

  return {
    billerNotes: readProcedureNoteSource(procedure, Extensions.ProcedureBillerNotes).trim(),
    providerNotes: readProcedureNoteSource(procedure, Extensions.ProcedureProviderNotes).trim(),
  };
}

/**
 * The picker session this row's Edit action opens.
 *
 * The code, modifiers and units are the row's own, so a session always starts from what the biller
 * is looking at. The notes are the row's own too — see {@link toRowNotes}. The Notes drawer reads
 * the same two values, which is what keeps the drawer and the edit session showing one answer.
 */
function toEditTarget({
  row,
  chargeItem,
  procedure,
  conditions,
}: {
  row: Pick<EncounterChargeItemRow, "procedureCode" | "codeDescription" | "modifiers" | "units">;
  chargeItem: ChargeItem;
  procedure: Procedure | undefined;
  conditions: ProcedureCodeEditTarget["conditions"];
}): ProcedureCodeEditTarget {
  return {
    conditions,
    procedure: {
      code: row.procedureCode,
      display: row.codeDescription,
      system: getCodeableConceptSystem(chargeItem.code),
      modifiers: row.modifiers,
      units: row.units,
      ...toRowNotes(chargeItem, procedure),
    },
  };
}

/**
 * Maps the encounter's captured charges into biller-section rows: one row per ChargeItem.
 *
 * The charge is the billing record of truth, so the code, description and units are read off it
 * rather than off the procedure it came from. Its modifiers are read off it too, once a biller has
 * edited it, including when that edit left it with none. Anything the charge does not carry comes
 * from the Procedure named on `supportingInformation`: the modifiers a provider recorded on a
 * charge nobody has edited, and the diagnoses on its `reasonReference`.
 *
 * A charge whose Procedure has since been retired still gets a row. It lists the charge's own
 * modifiers if it has any, and no diagnoses. Hiding it would lose a charge the encounter is going
 * to be billed for.
 *
 * A voided charge gets no row. Deleting a row is what voids a charge, so listing it again would undo
 * the deletion on screen. The charge is still on the server — see
 * {@link getActiveChargeItems} — and the encounter will not be billed for it.
 */
export function buildEncounterChargeItemRows({
  chargeItems,
  procedures,
  conditions,
}: BuildEncounterChargeItemRowsParams): EncounterChargeItemRow[] {
  const proceduresByReference = toResourcesByReference(procedures);
  const conditionsByReference = toResourcesByReference(conditions);

  return getActiveChargeItems(chargeItems).reduce<EncounterChargeItemRow[]>((rows, chargeItem) => {
    // A charge with no id cannot key a row, and the row's delete would have nothing to act on.
    if (!chargeItem.id) return rows;

    const procedure = findSourceProcedure(chargeItem, proceduresByReference);
    const conditions = toDiagnosisCodings(procedure, conditionsByReference);

    const row = {
      id: chargeItem.id,
      procedureCode: getCodeableConceptCode(chargeItem.code),
      codeDescription: getCodeableConceptDisplay(chargeItem.code),
      modifiers: resolveChargeItemModifiers(chargeItem, getProcedureModifiers(procedure)),
      diagnosisCodes: conditions.map(({ code }) => code),
      units: chargeItem.quantity?.value ?? DEFAULT_CHARGE_ITEM_ROW_UNITS,
    };

    rows.push({ ...row, editTarget: toEditTarget({ row, chargeItem, procedure, conditions }) });

    return rows;
  }, []);
}
